import {
	ContractBondSubtype,
	ContractSuretyType,
	isContractCategoryBondType,
	SuretyAccountId,
} from "@inrev/common";
import type { SuretyBondPreview } from "@portal/api/bond/agent";
import { useIsCommercialSuretyEnabled } from "@portal/hooks/useIsCommercialSuretyEnabled";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { BidBondSelector } from "@portal/views/admin/shared/submission/BidBondSelector";
import { type FC, useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormDropdown } from "../../../form/fields/FormDropdown";
import { Button } from "../../../primitives/Button";
import { useBondRequestSubmissionAdapter } from "../BondRequestSubmissionAdapterContext";
import {
	buildCommercialBondTypeOptions,
	LEGACY_ADMIN_BOND_TYPE_OPTIONS,
	LEGACY_BOND_TYPE_OPTIONS,
} from "../bondRequestBondTypeOptions";
import { PRINCIPAL_FIELDS, type SubmissionFormData } from "../bondRequestSubmissionFormSchema";
import { clearFieldsAfterBondType, clearFieldsAfterPrincipal } from "../clearSubmissionFields";
import { StepFieldLabel } from "./StepFieldLabel";

type PrincipalAndTypeStepProps = {
	accountId: SuretyAccountId;
	isAdmin?: boolean;
	onCancel: () => void;
	onNext: () => void;
	onBack?: () => void;
};

export const PrincipalAndTypeStep: FC<PrincipalAndTypeStepProps> = ({
	accountId,
	isAdmin,
	onCancel,
	onNext,
	onBack,
}) => {
	const { control, trigger, setValue, getValues, clearErrors } =
		useFormContext<SubmissionFormData>();
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const isCommercialSuretyEnabled = useIsCommercialSuretyEnabled();

	const { companies, companiesIsLoading } = useBondRequestSubmissionAdapter();

	const bondType = useWatch({ control, name: "bondType" });
	const principal = useWatch({ control, name: "principal" });
	const bidBondId = useWatch({ control, name: "bidBondId" });
	const isB2F = bondType === ContractSuretyType.bid_to_final;
	const showContractBondType = isCommercialSuretyEnabled && isContractCategoryBondType(bondType);

	const bondTypeOptions = useMemo(
		() =>
			isCommercialSuretyEnabled
				? buildCommercialBondTypeOptions(true)
				: isAdmin
					? LEGACY_ADMIN_BOND_TYPE_OPTIONS
					: LEGACY_BOND_TYPE_OPTIONS,
		[isCommercialSuretyEnabled, isAdmin],
	);

	const principalOptions = useMemo(
		() => (companies ?? []).map((c) => ({ label: c.name, value: c.id })),
		[companies],
	);

	const singlePrincipalId = companies?.length === 1 ? companies[0].id : undefined;

	useEffect(() => {
		if (singlePrincipalId && getValues("principal") !== singlePrincipalId) {
			setValue("principal", singlePrincipalId, { shouldValidate: true });
		}
	}, [singlePrincipalId, getValues, setValue]);

	const principalRef = useRef(principal);
	useEffect(() => {
		if (principalRef.current === principal) return;
		const previous = principalRef.current;
		principalRef.current = principal;
		if (previous === undefined) return;
		clearFieldsAfterPrincipal(setValue, clearErrors);
	}, [principal, setValue, clearErrors]);

	const bondTypeRef = useRef(bondType);
	useEffect(() => {
		if (bondTypeRef.current === bondType) return;
		bondTypeRef.current = bondType;
		clearFieldsAfterBondType(setValue, clearErrors, bondType);
	}, [bondType, setValue, clearErrors]);

	const handleSelectBidBond = (bondPreview?: SuretyBondPreview) => {
		if (!bondPreview) {
			setValue("bidBondId", undefined);
			setValue("actualBidAmount", undefined);
			setValue("actualBidDate", undefined);
			return;
		}
		setValue("bidBondId", bondPreview.id, { shouldValidate: true });
		setValue("actualBidAmount", bondPreview.amount, { shouldValidate: true });
		setValue("actualBidDate", bondPreview.effectiveDate, { shouldValidate: true });
		const projectNameSource = isMinimalUIEnabled ? bondPreview.name : bondPreview.description;
		if (projectNameSource && !getValues("projectName")) {
			setValue("projectName", projectNameSource);
		}
		if (bondPreview.agentId) {
			setValue("agentId", bondPreview.agentId);
		}
	};

	const handleNext = async () => {
		if (await trigger([...PRINCIPAL_FIELDS])) {
			onNext();
		}
	};

	return (
		<div className="flex flex-col gap-6 px-6 pt-6 pb-6">
			<div className="flex flex-col gap-3">
				<StepFieldLabel required>Principal</StepFieldLabel>
				<FormDropdown<SubmissionFormData>
					name="principal"
					control={control}
					testId="principal-dropdown"
					options={principalOptions}
					placeholder={companiesIsLoading ? "Loading principals..." : "Select principal"}
					anchor="bottom start"
					disabled={!!singlePrincipalId}
				/>
			</div>
			<div className="flex flex-col gap-3">
				<StepFieldLabel required>Bond Type</StepFieldLabel>
				<FormDropdown<SubmissionFormData>
					name="bondType"
					control={control}
					testId="bond-type-dropdown"
					options={bondTypeOptions}
					placeholder="Select bond type"
					anchor="bottom start"
				/>
			</div>
			{showContractBondType && (
				<div className="flex flex-col gap-3" data-testid="contract-bond-type-dropdown">
					<StepFieldLabel required>Contract Bond Type</StepFieldLabel>
					<FormDropdown<SubmissionFormData>
						name="contractBondType"
						control={control}
						testId="contract-bond-type-input"
						options={ContractBondSubtype.options()}
						placeholder="Select bond type"
						anchor="bottom start"
					/>
				</div>
			)}
			{isB2F && (
				<div className="flex flex-col gap-2" data-testid="bid-bond-selector">
					<StepFieldLabel required>Select Bid Bond</StepFieldLabel>
					<BidBondSelector
						accountId={accountId}
						onSelect={handleSelectBidBond}
						selectedBondId={bidBondId}
						buttonLabel="Select Bid Bond"
					/>
				</div>
			)}
			<div className="flex justify-end gap-2 pt-1">
				<Button color="secondary" onClick={onBack ?? onCancel}>
					{onBack ? "Back" : "Cancel"}
				</Button>
				<Button color="primary" onClick={handleNext} trackingId="bond-request-modal-principal-next">
					Next
				</Button>
			</div>
		</div>
	);
};
